import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import axios from 'axios';
import { clinicInfo, serviceLabels } from '../mock';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  service: '',
  message: '',
  preferredDate: ''
};

const buildWhatsAppUrl = (formData) => {
  const serviceLabel = serviceLabels[formData.service] || 'Consulta General';
  const lines = [
    `Hola, soy ${formData.name} y quisiera agendar una cita en Qara.`,
    `Servicio: ${serviceLabel}`,
    formData.preferredDate ? `Fecha preferida: ${formData.preferredDate}` : null,
    `Teléfono: ${formData.phone}`,
    formData.message ? `Mensaje: ${formData.message}` : null
  ].filter(Boolean);
  return `https://wa.me/${clinicInfo.whatsappNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
};

const SubmitButtonContent = ({ isSubmitting }) => {
  if (isSubmitting) {
    return (
      <span className="flex items-center justify-center">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-cream-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Enviando...
      </span>
    );
  }
  return (
    <span className="flex items-center justify-center">
      <Send className="w-5 h-5 mr-2" />
      Enviar Mensaje
    </span>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (formRef.current) observer.observe(formRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const whatsappWindow = window.open('', '_blank');

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        preferred_date: formData.preferredDate,
        message: formData.message
      });

      const whatsappUrl = buildWhatsAppUrl(formData);
      if (whatsappWindow) whatsappWindow.location.href = whatsappUrl;
      else window.open(whatsappUrl, '_blank');

      toast.success('Tu solicitud ha sido recibida. Te contactaremos pronto.');
      setFormData(EMPTY_FORM);
    } catch (error) {
      if (whatsappWindow) whatsappWindow.close();
      console.error('Error enviando solicitud:', error);
      toast.error(error.message || 'Hubo un error. Por favor inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card
      ref={formRef}
      className={`lg:col-span-2 border-[#E6E4DD] rounded-none shadow-none bg-white transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <CardHeader>
        <CardTitle className="text-3xl font-serif">Comencemos conociéndote</CardTitle>
        <p className="text-gray-600 mt-2 leading-relaxed">
          Completa tus datos. Nuestro equipo se pondrá en contacto contigo para agendar tu valoración personalizada.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre Completo *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Tu nombre"
                className="border-sage-200 focus:border-sage-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+593 99 123 4567"
                className="border-sage-200 focus:border-sage-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Correo Electrónico *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="tu@email.com"
              className="border-sage-200 focus:border-sage-400"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="service">Servicio de Interés</Label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="flex h-10 w-full rounded-md border border-sage-200 bg-white px-3 py-2 text-sm focus:border-sage-400 focus:outline-none focus:ring-2 focus:ring-sage-400 focus:ring-offset-2"
              >
                <option value="">Selecciona un servicio</option>
                <option value="mesoterapia">Mesoterapia Facial, Corporal y Capilar</option>
                <option value="skinboosters">Skinboosters</option>
                <option value="botox">Toxina Botulínica</option>
                <option value="peeling">Peelings de Grado Médico</option>
                <option value="alopecia">Manejo de Alopecia</option>
                <option value="labios">Perfilado de Labios</option>
                <option value="armonizacion">Armonización Facial</option>
                <option value="consulta">Consulta General</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="preferredDate">Fecha Preferida</Label>
              <Input
                id="preferredDate"
                name="preferredDate"
                type="date"
                value={formData.preferredDate}
                onChange={handleChange}
                className="border-sage-200 focus:border-sage-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Mensaje</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Cuéntanos sobre tus necesidades y expectativas..."
              rows={4}
              className="border-sage-200 focus:border-sage-400 resize-none"
            />
          </div>

          <Button
            type="submit"
            data-testid="contact-form-submit-button"
            disabled={isSubmitting}
            className="w-full bg-[#6E4F35] hover:bg-[#5a3f2a] text-cream-100 py-6 text-sm font-medium uppercase tracking-[0.15em] rounded-none transition-colors duration-300"
          >
            <SubmitButtonContent isSubmitting={isSubmitting} />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
